import {useMemo} from "react";
import {useQuery} from "@tanstack/react-query";
import {config} from "../config";
import {useAppContext} from "../libs/appContextLib";
import apiClient from "./apiClient";

export const SAVED_SCENARIOS_CACHE_TIME = 1000 * 60 * 60 * 36;
export const SAVED_SCENARIO_SUMMARY_STALE_TIME = 60 * 1000;
export const MAX_SELECTED_SCENARIOS = 10;

export const getSavedScenariosQueryKey = (customerId) => [`${customerId}_saved-scenarios`];
export const getSavedScenarioSummariesQueryKey = (customerId) => [
    ...getSavedScenariosQueryKey(customerId),
    'summary',
];
export const getSavedScenarioDetailsQueryKey = (customerId, scenarioIds = []) => [
    ...getSavedScenariosQueryKey(customerId),
    'detail',
    scenarioIds,
];

export const normalizeSavedScenarioIds = (scenarioIds = []) => {
    const values = Array.isArray(scenarioIds) ? scenarioIds : [scenarioIds];
    return [...new Set(values.filter((scenarioId) => (
        typeof scenarioId === 'string' && scenarioId.trim()
    )).map((scenarioId) => scenarioId.trim()))];
};

const getNextPageToken = (headers) => (
    headers?.get?.('x-qflow-next-token')
    || headers?.['x-qflow-next-token']
    || headers?.['X-QFlow-Next-Token']
    || null
);

const fetchSavedScenarioPages = async ({accountIdOverride = null, params: baseParams} = {}) => {
    const savedScenarios = [];
    const seenPageTokens = new Set();
    let pageToken = null;

    do {
        const params = {
            ...(baseParams || {}),
            ...(pageToken ? {page_token: pageToken} : {}),
        };
        const response = await apiClient.get(`${config.REACT_APP_BACKEND_HOST}/saved-scenarios`, {
            headers: accountIdOverride ? {'X-QFlow-Account-Id': accountIdOverride} : undefined,
            params: Object.keys(params).length > 0 ? params : undefined,
        });

        if (!Array.isArray(response.data)) {
            throw new Error('Invalid saved scenarios response');
        }

        savedScenarios.push(...response.data);
        pageToken = getNextPageToken(response.headers);

        if (pageToken && seenPageTokens.has(pageToken)) {
            throw new Error('Saved scenarios pagination returned a repeated token');
        }
        if (pageToken) {
            seenPageTokens.add(pageToken);
        }
    } while (pageToken);

    return savedScenarios;
};

// Shared across catalog consumers so simultaneous mounts cannot multiply the
// two-request detail limit. Each job is one backend request of at most ten IDs.
let activeDetailRequests = 0;
const detailRequestQueue = [];
const withDetailRequestSlot = (request) => new Promise((resolve, reject) => {
    const start = () => {
        activeDetailRequests += 1;
        Promise.resolve().then(request).then(resolve, reject).finally(() => {
            activeDetailRequests -= 1;
            detailRequestQueue.shift()?.();
        });
    };
    if (activeDetailRequests < 2) start();
    else detailRequestQueue.push(start);
});

export const fetchSavedScenarios = async ({accountIdOverride = null} = {}) => (
    fetchSavedScenarioPages({accountIdOverride})
);

export const fetchSavedScenarioSummaries = async ({accountIdOverride = null} = {}) => (
    fetchSavedScenarioPages({
        accountIdOverride,
        params: {view: 'summary'},
    })
);

export const fetchSavedScenariosByIds = async ({scenarioIds = [], accountIdOverride = null} = {}) => {
    const normalizedScenarioIds = normalizeSavedScenarioIds(scenarioIds);
    if (normalizedScenarioIds.length === 0) return [];
    const batches = [];
    for (let index = 0; index < normalizedScenarioIds.length; index += MAX_SELECTED_SCENARIOS) {
        batches.push(normalizedScenarioIds.slice(index, index + MAX_SELECTED_SCENARIOS));
    }
    const records = (await Promise.all(batches.map((ids) => withDetailRequestSlot(() => (
        fetchSavedScenarioPages({accountIdOverride, params: {scenario_ids: JSON.stringify(ids)}})
    ))))).flat();
    const byId = new Map(records.map((record) => [record.scenarioId, record]));
    return normalizedScenarioIds.map((id) => byId.get(id)).filter(Boolean);
};

export function useFetchSavedScenarios({accountIdOverride = null} = {}) {
    const {currUser} = useAppContext();
    const customerId = accountIdOverride || currUser?.customerId;

    return useQuery({
        queryKey: getSavedScenariosQueryKey(customerId),
        queryFn: () => fetchSavedScenarios({accountIdOverride}),
        staleTime: SAVED_SCENARIOS_CACHE_TIME, // or a high value if you want some automatic refetch
        gcTime: SAVED_SCENARIOS_CACHE_TIME, // 36 hour -- or until invalidated
        enabled: !!customerId,
    })
}

export function useFetchSavedScenarioSummaries({accountIdOverride = null, enabled = true} = {}) {
    const {currUser} = useAppContext();
    const customerId = accountIdOverride || currUser?.customerId;

    return useQuery({
        queryKey: getSavedScenarioSummariesQueryKey(customerId),
        queryFn: () => fetchSavedScenarioSummaries({accountIdOverride}),
        staleTime: SAVED_SCENARIO_SUMMARY_STALE_TIME,
        refetchOnMount: 'always',
        gcTime: SAVED_SCENARIOS_CACHE_TIME,
        enabled: Boolean(enabled && customerId),
    });
}

export function useFetchSavedScenariosByIds(
    scenarioIds,
    {accountIdOverride = null, enabled = true} = {},
) {
    const {currUser} = useAppContext();
    const customerId = accountIdOverride || currUser?.customerId;
    const normalizedScenarioIds = useMemo(
        () => normalizeSavedScenarioIds(scenarioIds).sort(),
        [scenarioIds],
    );

    return useQuery({
        queryKey: getSavedScenarioDetailsQueryKey(customerId, normalizedScenarioIds),
        queryFn: () => fetchSavedScenariosByIds({
            scenarioIds: normalizedScenarioIds,
            accountIdOverride,
        }),
        staleTime: SAVED_SCENARIOS_CACHE_TIME,
        gcTime: SAVED_SCENARIOS_CACHE_TIME,
        enabled: Boolean(enabled && customerId && normalizedScenarioIds.length > 0),
    });
}
